import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Star, Quote } from 'lucide-react';
import { BOX_IMAGES } from '@/data/images';
import { API } from '@/lib/api';

const EASE = [0.22, 1, 0.36, 1];

const FALLBACK = [
  { id: 'f1', name: 'Emma L.', role: 'Membre depuis 2 ans', text: "Le CrossFit ici m'a aidée à me sentir physiquement plus forte, mais surtout mentalement plus confiante. L'ambiance est incroyable.", rating: 5 },
  { id: 'f2', name: 'Marc D.', role: 'Membre depuis 1 an', text: 'Les coachs sont exceptionnels. Ils adaptent chaque exercice à mon niveau et me poussent à me dépasser chaque jour.', rating: 5 },
  { id: 'f3', name: 'Sophie B.', role: 'Membre depuis 6 mois', text: "J'hésitais à commencer le CrossFit. Aujourd'hui c'est ma passion. La communauté est soudée et bienveillante.", rating: 5 },
];

export const Testimonials = () => {
  const [items, setItems] = useState(FALLBACK);

  useEffect(() => {
    axios.get(`${API}/content/testimonials`).then((r) => {
      if (Array.isArray(r.data) && r.data.length > 0) setItems(r.data);
    }).catch(() => {});
  }, []);

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  return (
    <section
      data-testid="testimonials-section"
      className="relative overflow-hidden"
    >
      <div className="absolute inset-0">
        <img decoding="async"
          src={BOX_IMAGES.highFive}
          alt="Communauté CrossFit Saint-Chamond - esprit d'équipe"
          className="w-full h-full object-cover opacity-20"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-dark via-dark/95 to-primary-dark/30" />
        <div className="absolute inset-0 mesh-gradient opacity-40 pointer-events-none" />
      </div>

      <div className="container-custom relative z-10 py-24 md:py-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-10%' }}
          transition={{ duration: 0.9, ease: EASE }}
          className="text-center mb-16 md:mb-20"
        >
          <span className="text-primary-glow font-semibold text-xs tracking-[0.3em] uppercase">
            ━━ Témoignages
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl text-white mt-4 leading-[0.95]">
            ILS ONT TRANSFORMÉ
            <br />
            <span className="text-gradient-primary italic font-display inline-block pr-2">
              LEUR VIE
            </span>
          </h2>
        </motion.div>

        <div className={`grid md:grid-cols-${Math.min(items.length, 3)} gap-6`}>
          {items.slice(0, 6).map((item, idx) => (
            <motion.div
              key={item.id || idx}
              data-testid={`testimonial-card-${idx}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-10%' }}
              transition={{ duration: 0.8, delay: 0.15 + idx * 0.12, ease: EASE }}
              whileHover={{ y: -6 }}
              className="relative glass-card rounded-3xl p-8 transition-all duration-500 hover:border-primary-glow/30"
            >
              <Quote className="absolute top-6 right-6 h-8 w-8 text-primary-glow/20" strokeWidth={1.5} />

              <div className="flex gap-1 mb-5">
                {[...Array(item.rating || 5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-primary-glow text-primary-glow" />
                ))}
              </div>

              <p className="text-white/85 leading-relaxed mb-8 text-base">
                {item.text}
              </p>

              <div className="flex items-center gap-3 pt-6 border-t border-white/10">
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-primary-glow to-primary flex items-center justify-center ring-2 ring-primary-glow/20">
                  <span className="text-white font-bold text-sm">{getInitials(item.name)}</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">{item.name}</h3>
                  {item.role && <p className="text-white/50 text-xs tracking-wide">{item.role}</p>}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
